import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import axios from 'axios';
import './App.css'; // Ensure this import is present
import './FounderPanel.css';
import { personName, personOption, formatPersonOption, NO_MATCHES } from './personOption';
import { getBaseUrl } from './ApiContext';



const baseUrl = getBaseUrl();

const FounderPanel = ({ founders, onClose, onAddFounder, onRemoveFounder, currentCompanyId }) => {
    const [allPeople, setAllPeople] = useState([]);
    const [founderOptions, setFounderOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFounders, setSelectedFounders] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]); // Add state for selected options
    const [companyInfo, setCompanyInfo] = useState({}); // Add state for company info

    useEffect(() => {
        console.log('FounderPanel currentCompanyId:', currentCompanyId); // Debugging log
        fetchAllPeople();
        fetchCompanyInfo(); // Fetch company info when component mounts or currentCompanyId changes
    }, [currentCompanyId]);

    const fetchAllPeople = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/api/people`, {
                params: { fetch_all: true }
            });
            const people = Array.isArray(response.data.people) ? response.data.people : [];
            setAllPeople(people);
            setFounderOptions(people.map(personOption));
        } catch (error) {
            console.error('Error fetching people:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/companies/${currentCompanyId}`);
            setCompanyInfo(response.data);
        } catch (error) {
            console.error('Error fetching company info:', error);
        }
    };

    const linkedinUrl = (value) => {
        if (!value) return null;
        return value.startsWith('http') ? value : `https://${value}`;
    };

    const handleFounderSelect = (selectedOptions) => {
        console.log('currentCompanyId in handleFounderSelect:', currentCompanyId); // Debugging log
        console.log('selectedOptions:', selectedOptions); // Debugging log

        const selectedFounders = selectedOptions.map(option => {
            const person = allPeople.find(p => p.id === option.value);
            if (!person) {
                console.error('Person not found for option:', option); // Debugging log
                return null;
            }

            return {
                first_name: person.first_name,
                last_name: person.last_name,
                email: person.email,
                linkedin: person.linkedin,
                location: person.location,
                title: person.title
            };
        }).filter(founder => founder !== null);

        console.log('selectedFounders:', selectedFounders); // Debugging log
        setSelectedFounders(selectedFounders);
        setSelectedOptions(selectedOptions); // Update selected options state
    };

    const handleAddFounders = async () => {
        try {
            const addFounderPromises = selectedFounders.map(async (founder) => {
                const founderWithCompanyInfo = {
                    first_name: founder.first_name,
                    last_name: founder.last_name,
                    email: founder.email,
                    linkedin: founder.linkedin,
                    location: founder.location,
                    title: founder.title,
                    company_website: companyInfo.website, // Set company_website from company info
                    company_linkedin: companyInfo.linkedin // Set company_linkedin from company info
                };
                console.log('Sending founder data:', founderWithCompanyInfo); // Log the data being sent
                const response = await axios.post(`${baseUrl}/api/companies/${currentCompanyId}/add_person`, founderWithCompanyInfo);
                console.log('Response from adding founder:', response.data);
            });

            await Promise.all(addFounderPromises);

            setSelectedFounders([]); // Clear selected founders after adding
            setSelectedOptions([]); // Clear selected options in react-select
            fetchAllPeople(); // Re-fetch all people to update the panel
            await fetchCompanyFounders(); // Fetch updated list of company founders
        } catch (error) {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setSelectedFounders([]); // Clear selected founders after adding
            setSelectedOptions([]); // Clear selected options in react-select
            fetchAllPeople(); // Re-fetch all people to update the panel
            await fetchCompanyFounders(); // Fetch updated list of company founders
            console.error('Error adding founders:', error);
        }
    };

    const fetchCompanyFounders = async () => {
        try {
            const response = await axios.get(`${baseUrl}/api/companies/${currentCompanyId}/founders`);
            onAddFounder(response.data.founders); // Update the founders list
        } catch (error) {
            if (error.response && error.response.status === 404) {
                console.error('Company founders endpoint not found:', error);
            } else {
                console.error('Error fetching company founders:', error);
            }
        }
    };

    const handleRemoveFounder = async (founderId) => {
        try {
            await axios.delete(`${baseUrl}/api/companies/${currentCompanyId}/remove_person/${founderId}`);
            onRemoveFounder(founderId);
            fetchAllPeople(); // Re-fetch all people to update the panel
        } catch (error) {
            console.error('Error removing founder:', error);
        }
    };

    const addLabel =
        selectedFounders.length > 1
            ? `Add ${selectedFounders.length} founders`
            : 'Add founder';

    return (
        <div className="founder-panel">
            <div className="fp-header">
                <h2>Founders</h2>
                <button
                    type="button"
                    className="fp-close"
                    onClick={onClose}
                    aria-label="Close founders panel"
                >
                    &times;
                </button>
            </div>

            {founders.length === 0 ? (
                <p className="fp-empty">No founders on this company yet.</p>
            ) : (
                <ul className="fp-list">
                    {founders.map((founder, index) => {
                        const name = personName(founder);
                        const linkedin = linkedinUrl(founder.linkedin);
                        return (
                            <li key={founder.id || index} className="fp-card">
                                <div className="fp-card-head">
                                    <span className="fp-name">{name}</span>
                                    <button
                                        type="button"
                                        className="fp-remove"
                                        onClick={() => handleRemoveFounder(founder.id)}
                                        aria-label={`Remove ${name}`}
                                    >
                                        Remove
                                    </button>
                                </div>
                                {founder.title && <p className="fp-title">{founder.title}</p>}
                                {(founder.email || linkedin) && (
                                    <div className="fp-links">
                                        {founder.email && (
                                            <a className="fp-link" href={`mailto:${founder.email}`}>
                                                {founder.email}
                                            </a>
                                        )}
                                        {linkedin && (
                                            <a
                                                className="fp-link"
                                                href={linkedin}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                LinkedIn
                                            </a>
                                        )}
                                    </div>
                                )}
                                {founder.location && <p className="fp-meta">{founder.location}</p>}
                            </li>
                        );
                    })}
                </ul>
            )}

            <div className="fp-add">
                <label className="fp-label" htmlFor="founder-select">
                    Add founders
                </label>
                <Select
                    inputId="founder-select"
                    isMulti
                    name="founders"
                    options={founderOptions}
                    value={selectedOptions}
                    onChange={handleFounderSelect}
                    formatOptionLabel={formatPersonOption}
                    placeholder="Search people"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isLoading={isLoading}
                    loadingMessage={() => "Loading..."}
                    noOptionsMessage={NO_MATCHES}
                />
                <button
                    type="button"
                    className="fp-primary"
                    onClick={handleAddFounders}
                    disabled={selectedFounders.length === 0}
                >
                    {addLabel}
                </button>
            </div>
        </div>
    );
};

export default FounderPanel;